/**
 * Home — landing screen.
 *
 * Two large ChalkButtons (CREATE / JOIN) under the wordmark. If the player
 * has any in-progress sessions discoverable from localStorage tokens, a
 * "Resume Game" panel is rendered above the primary CTAs so they can
 * rejoin without losing their seat.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { SlateBackground } from '../components/base/SlateBackground';
import { ChalkButton } from '../components/base/ChalkButton';
import { prewarmApi } from '../net/apiFetch';
import { useResumableSessions, type ResumableSession } from '../hooks/useResumableSessions';
import { HowToPlayModal, HOW_TO_PLAY_EVENT } from '../components/play/HowToPlayModal';
import { useIsMobile } from '../hooks/useIsMobile';

function getApiBaseUrlForPrewarm(): string {
  const env = (import.meta as unknown as {
    env?: { VITE_API_BASE_URL?: string; VITE_WS_BASE_URL?: string };
  }).env;
  if (env?.VITE_API_BASE_URL) return env.VITE_API_BASE_URL;
  if (env?.VITE_WS_BASE_URL) return env.VITE_WS_BASE_URL.replace(/^ws/, 'http');
  return 'http://localhost:8787';
}

export function Home() {
  const navigate = useNavigate();
  const { sessions, loading, forget } = useResumableSessions();
  const isMobile = useIsMobile();

  // Pre-warm the deployed Worker on first paint of the home page so by
  // the time the user clicks CREATE GAME the Worker is already hot —
  // their POST /api/sessions doesn't pay cold-start latency.
  useEffect(() => {
    prewarmApi(getApiBaseUrlForPrewarm());
  }, []);

  return (
    <SlateBackground showWorldMap>
      <button
        type="button"
        onClick={() => {
          // ensure the modal opens even if they've seen it
          window.dispatchEvent(new Event(HOW_TO_PLAY_EVENT));
        }}
        style={{
          position: 'absolute',
          top: isMobile ? 12 : 24,
          right: isMobile ? 12 : 24,
          background: surfaces.waxRed,
          border: `2px solid ${surfaces.paper}`,
          padding: isMobile ? '8px 14px' : '12px 24px',
          borderRadius: 24,
          color: surfaces.paper,
          fontFamily: fontStacks.display,
          fontSize: isMobile ? 13 : 16,
          fontWeight: 800,
          cursor: 'pointer',
          boxShadow: '0 6px 0 rgba(0,0,0,0.3), 0 8px 16px rgba(0,0,0,0.4)',
          zIndex: 10,
        }}
      >
        How to Play
      </button>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100dvh',
          padding: '32px 24px',
          textAlign: 'center',
        }}
      >
        <header style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h1
            style={{
              fontFamily: fontStacks.display,
              fontSize: 'clamp(38px, 9vw, 72px)',
              fontWeight: 900,
              color: surfaces.paper,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              textShadow: `0 8px 0 ${surfaces.slateShadow}, 0 16px 32px rgba(0,0,0,0.5)`,
              margin: '0 0 16px 0',
              lineHeight: 1,
            }}
          >
            Quality Domination
          </h1>
          <p
            style={{
              margin: 0,
              fontFamily: fontStacks.body,
              fontSize: 'clamp(15px, 4vw, 20px)',
              fontWeight: 600,
              color: surfaces.paper,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              opacity: 0.9,
              width: '100%',
              maxWidth: 'min(600px, 92vw)',
            }}
          >
            A Global Strategy Game for the ISO/IEC/IEEE 29119-3 software-testing
            standard. Conquer territory by answering questions about
            documentation hierarchy.
          </p>
        </header>

        {!loading && sessions.length > 0 ? (
          <ResumePanel sessions={sessions} onForget={forget} />
        ) : null}

        {isMobile ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              alignItems: 'stretch',
              width: '100%',
              maxWidth: 'min(420px, 92vw)',
              marginTop: 24,
              zIndex: 10,
            }}
          >
            <ChalkButton
              size="lg"
              variant="primary"
              onClick={() => navigate('/create')}
              style={{ fontSize: 24, padding: '20px 28px', borderRadius: 20, boxShadow: '0 8px 0 rgba(0,0,0,0.3), 0 14px 28px rgba(0,0,0,0.5)' }}
            >
              CREATE GAME
            </ChalkButton>
            <ChalkButton
              size="lg"
              variant="secondary"
              onClick={() => navigate('/join')}
              style={{ fontSize: 22, padding: '18px 24px', borderRadius: 20, background: '#1d4ed8', color: surfaces.paper, borderColor: surfaces.chalk, boxShadow: '0 8px 0 rgba(0,0,0,0.4), 0 14px 24px rgba(0,0,0,0.5)' }}
            >
              JOIN GAME
            </ChalkButton>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
              height: 250,
              width: '100%',
              marginTop: 40,
              zIndex: 10,
            }}
          >
            <div style={{ position: 'absolute', transform: 'rotate(-4deg) translateX(-180px) translateY(-10px)', zIndex: 2 }}>
              <ChalkButton
                size="lg"
                variant="primary"
                onClick={() => navigate('/create')}
                style={{ fontSize: 36, padding: '32px 48px', borderRadius: 24, boxShadow: '0 12px 0 rgba(0,0,0,0.3), 0 20px 40px rgba(0,0,0,0.5)' }}
              >
                CREATE GAME
              </ChalkButton>
            </div>
            <div style={{ position: 'absolute', transform: 'rotate(6deg) translateX(180px) translateY(30px)', zIndex: 1 }}>
              <ChalkButton
                size="lg"
                variant="secondary"
                onClick={() => navigate('/join')}
                style={{ fontSize: 32, padding: '28px 40px', borderRadius: 24, background: '#1d4ed8', color: surfaces.paper, borderColor: surfaces.chalk, boxShadow: '0 10px 0 rgba(0,0,0,0.4), 0 16px 32px rgba(0,0,0,0.5)' }}
              >
                JOIN GAME
              </ChalkButton>
            </div>
          </div>
        )}

        <footer
          style={{
            marginTop: 96,
            fontFamily: fontStacks.mono,
            fontSize: 12,
            color: surfaces.chalk,
            opacity: 0.4,
            letterSpacing: '0.18em',
          }}
        >
          v0.0.1 · phase 1 + 3 scaffold
        </footer>
      </div>
      <HowToPlayModal />
    </SlateBackground>
  );
}

function ResumePanel({
  sessions,
  onForget,
}: {
  sessions: readonly ResumableSession[];
  onForget: (sessionId: string) => void;
}) {
  const navigate = useNavigate();
  return (
      <section
      aria-label="Resume in-progress games"
      style={{
        marginBottom: 48,
        padding: '24px 32px',
        background: 'rgba(0, 0, 0, 0.4)',
        border: `4px solid ${surfaces.chalk}`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.5)`,
        borderRadius: 16,
        width: '100%',
        maxWidth: 'min(640px, 92vw)',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 10
      }}
    >
      <p
        style={{
          margin: 0,
          marginBottom: 16,
          fontFamily: fontStacks.display,
          fontSize: 20,
          letterSpacing: '0.06em',
          color: surfaces.chalk,
        }}
      >
        Unfinished business
      </p>
      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {sessions.map((s) => {
          const target =
            s.phase === 'playing'
              ? `/game/${s.sessionId}/play`
              : `/game/${s.sessionId}/lobby`;
          return (
            <li
              key={s.sessionId}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                justifyContent: 'space-between',
              }}
            >
              <div
                style={{
                  textAlign: 'left',
                  fontFamily: fontStacks.mono,
                  color: surfaces.chalk,
                  fontSize: 14,
                  letterSpacing: '0.18em',
                }}
              >
                <span>{s.sessionId}</span>
                <span style={{ marginLeft: 12, opacity: 0.55 }}>
                  · {s.phase === 'playing' ? 'in match' : s.phase === 'lobby' ? 'in lobby' : 'last seen'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <ChalkButton
                  size="sm"
                  variant="primary"
                  onClick={() => navigate(target)}
                >
                  Resume
                </ChalkButton>
                <ChalkButton
                  size="sm"
                  variant="secondary"
                  onClick={() => onForget(s.sessionId)}
                >
                  Forget
                </ChalkButton>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
